import { useState } from 'react';
import { Modal, Button, Form, Spinner, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import { utils, writeFile } from 'xlsx';

/**
 * Get the appropriate FC value based on selected method.
 * Mirrors getFCByMethod from FCTable.js so exports match the display.
 */
function getExportFC(row, fcMethod) {
    switch (fcMethod) {
        case 'SSN':
            return row.fcOptions?.ssn?.fc ?? null;
        case 'MS':
            return row.fcOptions?.ms?.fc ?? null;
        case 'BLENDED':
            return row.blendedFC ?? row.fcOptions?.blended?.fc ?? null;
        case 'SS1':
        default:
            return row.suggestedFC;
    }
}

/**
 * Export Modal for FC Optimizer
 * Prompts user to choose between exporting all filtered results or only selected items
 */
export default function ExportModal({
    show,
    onHide,
    filters,
    timeFrame,
    selectedRows,
    recommendations,
    totalFilteredCount,
    onExportComplete,
    fetchAllForExport,  // Function from useFCRecommendations hook
    fcMethod = 'SS1'    // Active FC method — export must match display
}) {
    const [exportOption, setExportOption] = useState('all');
    const [format, setFormat] = useState('standard');
    const [exporting, setExporting] = useState(false);

    const selectedCount = selectedRows?.length || 0;
    const hasSelection = selectedCount > 0;

    // Reset state when modal opens
    const handleShow = () => {
        setExportOption(hasSelection ? 'selected' : 'all');
        setFormat('standard');
        setExporting(false);
    };

    const handleExport = async () => {
        setExporting(true);

        try {
            if (exportOption === 'selected') {
                // Export selected items from local data
                await exportSelectedItems();
            } else {
                // Export all filtered items using the same logic as UI display
                await exportAllFiltered();
            }
            onHide();
        } catch (error) {
            console.error('Export error:', error);
            toast.error('Failed to export data. Check console for details.');
        } finally {
            setExporting(false);
        }
    };

    /**
     * Export only selected items (uses local recommendations data)
     */
    const exportSelectedItems = async () => {
        const dataToExport = recommendations.filter(r => selectedRows.includes(r.sku));

        if (dataToExport.length === 0) {
            toast.warning('No selected items to export');
            return;
        }

        const exportData = formatExportData(dataToExport, format);
        downloadExport(exportData, format);
        toast.success(`Exported ${dataToExport.length} selected items`);
        onExportComplete?.();
    };

    /**
     * Export all filtered items using the same transformation as UI
     * This ensures export matches exactly what users see in the table
     */
    const exportAllFiltered = async () => {
        if (!fetchAllForExport) {
            toast.error('Export function not available');
            return;
        }

        // Fetch all data with the same transformations as the UI
        const allData = await fetchAllForExport();

        if (!allData || allData.length === 0) {
            toast.warning('No data to export with current filters');
            return;
        }

        const exportData = formatExportData(allData, format);
        downloadExport(exportData, format);
        toast.success(`Exported ${allData.length} items`);
        onExportComplete?.();
    };

    /**
     * Format local data for export based on selected format.
     * Uses getExportFC to match the active FC method shown on screen.
     */
    const formatExportData = (data, format) => {
        // Helper: compute method-aware values for a row
        const methodValues = (r) => {
            const fc = getExportFC(r, fcMethod);
            const delta = (fc != null && r.currentFC != null) ? fc - r.currentFC : null;
            let rec;
            if (r.currentFC == null) {
                rec = 'Increase';
            } else if (delta > 0.5) {
                rec = 'Increase';
            } else if (delta < -0.5) {
                rec = 'Decrease';
            } else {
                rec = 'No Change';
            }
            return { fc, delta, rec };
        };

        switch (format) {
            case 'cartcom':
                return data.map(r => {
                    const mv = methodValues(r);
                    return {
                        'SKU': r.sku,
                        'FulfillmentCost': mv.fc?.toFixed(2) || '',
                        'Notes': `${mv.rec}: ${r.priority} priority`
                    };
                });

            case 'bulk':
                return data.map(r => {
                    const mv = methodValues(r);
                    return {
                        'SKU': r.sku,
                        'Current FC': r.currentFC?.toFixed(2) || 'N/A',
                        'Suggested FC': mv.fc?.toFixed(2) || '',
                        'Delta': mv.delta?.toFixed(2) || '',
                        'Priority': r.priority,
                        'Confidence': r.confidence,
                        'Sample Size': r.sampleSize
                    };
                });

            default: // standard
                return data.map(r => {
                    const mv = methodValues(r);
                    return {
                        'SKU': r.sku,
                        'Product': r.title || '',
                        'Current FC': r.currentFC?.toFixed(2) || 'N/A',
                        'Avg Cost': r.avgCost?.toFixed(2) || '',
                        'Suggested FC': mv.fc?.toFixed(2) || '',
                        'Delta': mv.delta?.toFixed(2) || '',
                        'Recommendation': mv.rec,
                        'Priority': r.priority,
                        'Confidence': r.confidence,
                        'Sample Size': r.sampleSize,
                        'Min Cost': r.minCost?.toFixed(2) || '',
                        'Max Cost': r.maxCost?.toFixed(2) || '',
                        'Std Dev': r.stdDev?.toFixed(2) || ''
                    };
                });
        }
    };

    /**
     * Download export data as Excel file
     */
    const downloadExport = (data, format) => {
        const fileNames = {
            standard: 'fc_recommendations.xlsx',
            cartcom: 'fc_update_cartcom.xlsx',
            bulk: 'fc_bulk_update.xlsx'
        };

        const ws = utils.json_to_sheet(data);
        const wb = utils.book_new();
        utils.book_append_sheet(wb, ws, 'Recommendations');
        writeFile(wb, fileNames[format] || 'fc_export.xlsx');
    };

    return (
        <Modal show={show} onHide={onHide} onShow={handleShow} centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    <FontAwesomeIcon icon="file-export" className="me-2" />
                    Export Recommendations
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {/* Export Option Selection */}
                <Form.Group className="mb-4">
                    <Form.Label className="fw-semibold mb-3">What would you like to export?</Form.Label>

                    <div className="d-flex flex-column gap-2">
                        <div
                            className={`p-3 rounded-2 cursor-pointer ${exportOption === 'all' ? 'bg-primary bg-opacity-10 border border-primary' : 'bg-light border'}`}
                            onClick={() => setExportOption('all')}
                            style={{ cursor: 'pointer' }}
                        >
                            <Form.Check
                                type="radio"
                                id="export-all"
                                name="exportOption"
                                label={
                                    <span>
                                        <strong>Export All</strong>
                                        <span className="text-muted ms-2">
                                            ({totalFilteredCount || 'all'} items matching current filters)
                                        </span>
                                    </span>
                                }
                                checked={exportOption === 'all'}
                                onChange={() => setExportOption('all')}
                                className="m-0"
                            />
                        </div>

                        <div
                            className={`p-3 rounded-2 ${!hasSelection ? 'opacity-50' : ''} ${exportOption === 'selected' ? 'bg-primary bg-opacity-10 border border-primary' : 'bg-light border'}`}
                            onClick={() => hasSelection && setExportOption('selected')}
                            style={{ cursor: hasSelection ? 'pointer' : 'not-allowed' }}
                        >
                            <Form.Check
                                type="radio"
                                id="export-selected"
                                name="exportOption"
                                label={
                                    <span>
                                        <strong>Export Selected</strong>
                                        <span className={`ms-2 ${hasSelection ? 'text-primary fw-semibold' : 'text-muted'}`}>
                                            ({selectedCount} items selected)
                                        </span>
                                    </span>
                                }
                                checked={exportOption === 'selected'}
                                onChange={() => setExportOption('selected')}
                                disabled={!hasSelection}
                                className="m-0"
                            />
                        </div>
                    </div>
                </Form.Group>

                {/* Format Selection */}
                <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold">Export Format</Form.Label>
                    <Form.Select
                        value={format}
                        onChange={(e) => setFormat(e.target.value)}
                    >
                        <option value="standard">Standard (Full Details)</option>
                        <option value="cartcom">Cart.com Format (SKU, FC, Notes)</option>
                        <option value="bulk">Bulk Update (SKU, Current/Suggested FC, Delta)</option>
                    </Form.Select>
                </Form.Group>

                {/* Info Alert */}
                {exportOption === 'all' && (
                    <Alert variant="info" className="mb-0 py-2 fs--1">
                        <FontAwesomeIcon icon="info-circle" className="me-2" />
                        This will export all recommendations matching your current filters
                    </Alert>
                )}
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={onHide} disabled={exporting}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={handleExport}
                    disabled={exporting || (exportOption === 'selected' && !hasSelection)}
                >
                    {exporting ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Exporting...
                        </>
                    ) : (
                        <>
                            <FontAwesomeIcon icon="download" className="me-2" />
                            Export {exportOption === 'selected' ? `${selectedCount} Items` : 'All'}
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
